import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
    getPendingReviewProposals,
    approveProposal,
    rejectProposal,
    requestChanges
} from '../services/proposalService';
import { AdminPageLayout } from './AdminPageLayout';
import { AdminCard } from './AdminCard';

const getProposalSubmittedDate = (proposal) => {
    if (!proposal) return null;
    return proposal.proposalSentAt || proposal.submittedAt || proposal.createdAt;
};

const formatProposalDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const formatProposalPricing = (proposal) => {
    const amount = proposal?.pricing?.amount;
    const currency = proposal?.pricing?.currency || '';
    if (amount === null || amount === undefined) return 'N/A';
    return `$${amount.toLocaleString()} ${currency}`.trim();
};

export function ProposalReviewPanel() {
    const [proposals, setProposals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedProposal, setSelectedProposal] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pages: 1,
        total: 0
    });

    useEffect(() => {
        loadPendingProposals();
    }, [pagination.current]);

    const loadPendingProposals = async () => {
        try {
            setLoading(true);
            const response = await getPendingReviewProposals({
                page: pagination.current,
                limit: 10
            });

            if (response.success) {
                setProposals(response.data);
                setPagination(response.pagination);
            } else {
                setError(response.error || 'Failed to load pending proposals');
            }
        } catch (error) {
            setError(error.message || 'Failed to load pending proposals');
        } finally {
            setLoading(false);
        }
    };

    const handleReviewAction = async (action, proposalId, feedback = '') => {
        try {
            setActionLoading(true);
            let response;

            switch (action) {
                case 'approve':
                    response = await approveProposal(proposalId, feedback);
                    break;
                case 'reject':
                    response = await rejectProposal(proposalId, feedback);
                    break;
                case 'request_changes':
                    response = await requestChanges(proposalId, feedback);
                    break;
                default:
                    throw new Error('Invalid action');
            }

            if (response.success) {
                setShowModal(false);
                setSelectedProposal(null);
                loadPendingProposals();
            } else {
                setError(response.error || `Failed to ${action} proposal`);
            }
        } catch (error) {
            setError(error.message || `Failed to ${action} proposal`);
        } finally {
            setActionLoading(false);
        }
    };

    const openReviewModal = (proposal) => {
        setSelectedProposal(proposal);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedProposal(null);
    };

    if (loading) {
        return (
            <AdminPageLayout>
                <AdminCard bodyClassName="flex items-center justify-center h-64">
                    <div className="text-gray-500">Loading pending proposals...</div>
                </AdminCard>
            </AdminPageLayout>
        );
    }

    return (
        <AdminPageLayout>
            <AdminCard bodyClassName="space-y-4">
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                        {error}
                    </div>
                )}

                {proposals.length === 0 ? (
                    <div className="text-center py-16">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <h3 className="text-base font-medium text-gray-900 mb-1">No proposals pending review</h3>
                        <p className="text-sm text-gray-500 max-w-sm mx-auto">
                            When team members submit proposals for review, they'll appear here for your approval.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {proposals.map((proposal) => (
                            <div key={proposal._id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <h3 className="text-lg font-semibold text-gray-900">{proposal.title}</h3>
                                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                                                Ready for Review
                                            </span>
                                        </div>

                                        <p className="text-gray-600 text-sm mb-2 line-clamp-2">{proposal.description || 'No description provided.'}</p>

                                        <div className="flex items-center gap-4 text-sm text-gray-500 mb-2">
                                            <span><strong>User:</strong> {proposal.userId?.name || 'N/A'}</span>
                                            <span><strong>Job:</strong> {proposal.jobPostingId?.title || 'N/A'}</span>
                                            <span><strong>Budget:</strong> {formatProposalPricing(proposal)}</span>
                                            <span><strong>Submitted:</strong> {formatProposalDateTime(getProposalSubmittedDate(proposal))}</span>
                                        </div>

                                        <div className="text-sm text-gray-600">
                                            <strong>Cover Letter Preview:</strong>{' '}
                                            {proposal.coverLetter ? `${proposal.coverLetter.substring(0, 200)}...` : 'No cover letter'}
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-2 ml-4">
                                        <button
                                            onClick={() => openReviewModal(proposal)}
                                            className="bg-primary-600 text-white px-3 py-1.5 rounded-md hover:bg-primary-700 text-sm font-medium transition-colors"
                                        >
                                            Review
                                        </button>
                                        <button
                                            onClick={() => window.open(`/proposals/${proposal._id}`, '_blank')}
                                            className="text-gray-600 hover:text-gray-800 text-sm"
                                        >
                                            View Full
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {pagination.pages > 1 && (
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                        <div className="text-xs text-gray-600">
                            Showing <span className="font-medium text-gray-900">{((pagination.current - 1) * 10) + 1}</span> to <span className="font-medium text-gray-900">{Math.min(pagination.current * 10, pagination.total)}</span> of <span className="font-medium text-gray-900">{pagination.total}</span> proposals
                        </div>
                        <div className="flex gap-1">
                            <button
                                onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
                                disabled={pagination.current === 1}
                                className="px-3 py-1.5 text-sm border border-gray-200 rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                            >
                                Previous
                            </button>
                            <span className="px-3 py-1.5 text-sm text-gray-700">
                                Page {pagination.current} of {pagination.pages}
                            </span>
                            <button
                                onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
                                disabled={pagination.current === pagination.pages}
                                className="px-3 py-1.5 text-sm border border-gray-200 rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </AdminCard>

            {/* Review Modal */}
            {showModal && selectedProposal && (
                <ProposalReviewModal
                    proposal={selectedProposal}
                    onClose={closeModal}
                    onAction={handleReviewAction}
                    loading={actionLoading}
                />
            )}
        </AdminPageLayout>
    );
}

function ProposalReviewModal({ proposal, onClose, onAction, loading }) {
    const [action, setAction] = useState('');
    const [feedback, setFeedback] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (action && (action === 'approve' || feedback.trim())) {
            onAction(action, proposal._id, feedback);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl border border-gray-200 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Review Proposal</h3>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="space-y-6">
                        {/* Proposal Details */}
                        <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900 mb-2">{proposal.title}</h4>
                            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                                <div><strong>User:</strong> {proposal.userId?.name}</div>
                                <div><strong>Email:</strong> {proposal.userId?.email}</div>
                                <div><strong>Job:</strong> {proposal.jobPostingId?.title}</div>
                                <div><strong>Budget:</strong> {formatProposalPricing(proposal)}</div>
                                <div><strong>Type:</strong> {proposal.pricing?.type || 'N/A'}</div>
                                <div><strong>Submitted:</strong> {formatProposalDateTime(getProposalSubmittedDate(proposal))}</div>
                            </div>
                        </div>

                        {/* Cover Letter */}
                        <div>
                            <h4 className="font-semibold text-gray-900 mb-2">Cover Letter</h4>
                            <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{proposal.coverLetter}</p>
                            </div>
                        </div>

                        {/* Attachments */}
                        {proposal.attachments && proposal.attachments.length > 0 && (
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-2">Attachments</h4>
                                <div className="space-y-2">
                                    {proposal.attachments.map((attachment, index) => (
                                        <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                                            <span>📎</span>
                                            <span>{attachment.name}</span>
                                            <span className="text-gray-400">({(attachment.size / 1024).toFixed(1)} KB)</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Portfolio Items */}
                        {proposal.portfolioItems && proposal.portfolioItems.length > 0 && (
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-2">Portfolio Items</h4>
                                <div className="space-y-2">
                                    {proposal.portfolioItems.map((item, index) => (
                                        <div key={index} className="bg-gray-50 rounded-lg p-3">
                                            <div className="font-medium text-sm">{item.title}</div>
                                            {item.description && <div className="text-sm text-gray-600 mt-1">{item.description}</div>}
                                            {item.url && <div className="text-sm text-primary-600 mt-1">{item.url}</div>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Links */}
                        {proposal.links && proposal.links.length > 0 && (
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-2">Links</h4>
                                <div className="space-y-2">
                                    {proposal.links.map((link, index) => (
                                        <div key={index} className="bg-gray-50 rounded-lg p-3">
                                            <div className="font-medium text-sm">{link.title}</div>
                                            <div className="text-sm text-primary-600">{link.url}</div>
                                            {link.description && <div className="text-sm text-gray-600 mt-1">{link.description}</div>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Screening Questions */}
                        {proposal.screeningAnswers && proposal.screeningAnswers.length > 0 && (
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-2">Screening Questions</h4>
                                <div className="space-y-3">
                                    {proposal.screeningAnswers.map((qa, index) => (
                                        <div key={index} className="bg-gray-50 rounded-lg p-3">
                                            <div className="font-medium text-sm text-gray-900 mb-1">{qa.question}</div>
                                            <div className="text-sm text-gray-700">{qa.answer}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Review Actions */}
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-2">
                                    Review Action
                                </label>
                                <div className="space-y-2">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="action"
                                            value="approve"
                                            checked={action === 'approve'}
                                            onChange={(e) => setAction(e.target.value)}
                                            className="mr-2 text-primary-600 focus:ring-primary-500"
                                        />
                                        <span className="text-success-700 text-sm font-medium">Approve</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="action"
                                            value="reject"
                                            checked={action === 'reject'}
                                            onChange={(e) => setAction(e.target.value)}
                                            className="mr-2 text-primary-600 focus:ring-primary-500"
                                        />
                                        <span className="text-red-700 text-sm font-medium">Reject</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="action"
                                            value="request_changes"
                                            checked={action === 'request_changes'}
                                            onChange={(e) => setAction(e.target.value)}
                                            className="mr-2 text-primary-600 focus:ring-primary-500"
                                        />
                                        <span className="text-accent-700 text-sm font-medium">Request Changes</span>
                                    </label>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Comments / Feedback
                                </label>
                                <textarea
                                    value={feedback}
                                    onChange={(e) => setFeedback(e.target.value)}
                                    rows={4}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                    placeholder="Provide feedback or comments for the user..."
                                />
                            </div>

                            <div className="flex gap-3 pt-4 border-t border-gray-100">
                                <button
                                    type="submit"
                                    disabled={loading || !action || (action !== 'approve' && !feedback.trim())}
                                    className={`px-3 py-1.5 rounded-md text-white text-sm font-medium transition-colors ${action === 'approve'
                                        ? 'bg-success-600 hover:bg-success-700'
                                        : action === 'reject'
                                            ? 'bg-red-600 hover:bg-red-700'
                                            : 'bg-accent-500 hover:bg-accent-600'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                    {loading ? 'Processing...' :
                                        action === 'approve' ? 'Approve Proposal'
                                            : action === 'reject' ? 'Reject Proposal'
                                                : 'Request Changes'}
                                </button>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}
